<template>
  <div class="bg-white p-4">
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-lg font-semibold text-gray-800">Parâmetros de Análise</h3>
      <div class="flex items-center space-x-2">
        <button @click="resetConfig" class="hover:text-blue-800 text-sm text-red-600">Resetar</button>
        <button
          @click="executeWithCurrentConfig"
          :disabled="isAnalyzing"
          class="bg-blue-600 px-3 py-1 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
          <span v-if="isAnalyzing" class="flex items-center">
            <svg class="mr-1 h-3 w-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Analisando...
          </span>
          <span v-else>Executar</span>
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
      <!-- Duração Mínima do Ciclo -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Duração Mínima (segundos) </label>
        <input
          v-model.number="config.min_cycle_duration"
          type="number"
          min="1"
          max="1000"
          class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Duração Máxima do Ciclo -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Duração Máxima (segundos) </label>
        <input
          v-model.number="config.max_cycle_duration"
          type="number"
          min="1"
          max="1000"
          class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Percentil de Mudança -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Percentil de Mudança (%) </label>
        <input
          v-model.number="config.change_percentile"
          type="number"
          min="0"
          max="100"
          step="1"
          class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Pontos Mínimos de Dados -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Pontos Mínimos de Dados </label>
        <input
          v-model.number="config.min_data_points"
          type="number"
          min="1"
          max="100"
          class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Fator de Suavização -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Fator de Suavização </label>
        <input
          v-model.number="config.smoothing_factor"
          type="number"
          min="0.1"
          max="10"
          step="0.1"
          class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Multiplicador de Taxa -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Multiplicador de Taxa </label>
        <input
          v-model.number="config.rate_multiplier"
          type="number"
          min="0.1"
          max="20"
          step="0.1"
          class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Período da Janela -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Período da Janela </label>
        <select v-model="config.window_period" class="w-full border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="1s">1 segundo</option>
          <option value="5s">5 segundos</option>
          <option value="10s">10 segundos</option>
          <option value="30s">30 segundos</option>
          <option value="1m">1 minuto</option>
        </select>
      </div>

      <!-- Média dos Vizinhos -->
      <div class="space-y-2">
        <label class="block text-sm font-medium text-gray-700"> Usar Média dos Vizinhos </label>
        <div class="flex items-center">
          <input v-model="config.use_neighbor_average" type="checkbox" class="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500" />
          <span class="ml-2 text-sm text-gray-600">
            {{ config.use_neighbor_average ? 'Sim' : 'Não' }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CycleAnalysisConfig } from '@/composables/useCycleAnalysis'
import { useRegisterStore } from '@/store/register'
import { computed, onMounted, watch } from 'vue'

interface Props {
  config: CycleAnalysisConfig
  isAnalyzing?: boolean
  sensorData?: any
  sensorIndex?: number
}

interface Emits {
  (e: 'update:config', config: CycleAnalysisConfig): void
  (e: 'cancel'): void
  (e: 'execute', config: CycleAnalysisConfig): void
  (e: 'sensor-updated'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const registerStore = useRegisterStore()

const config = computed({
  get: () => {
    return props.config
  },
  set: (value) => {
    emit('update:config', value)
  },
})

// Função para carregar mixing settings do sensor
const loadSensorMixingSettings = async () => {
  if (!props.sensorData?.sensor?.id) return

  try {
    const settings = registerStore.loadMixingCycleSettings(props.sensorData.sensor.id)

    if (settings) {
      // Atualizar configuração com os dados do sensor
      const updatedConfig = {
        window_period: settings.window_period || '1m',
        min_cycle_duration: settings.min_cycle_duration || 20,
        max_cycle_duration: settings.max_cycle_duration || 350,
        change_percentile: settings.change_percentile || 70,
        min_data_points: settings.min_data_points || 10,
        smoothing_factor: settings.smoothing_factor || 2.0,
        use_neighbor_average: settings.use_neighbor_average !== undefined ? settings.use_neighbor_average : true,
        rate_multiplier: settings.rate_multiplier || 2.0,
      }

      emit('update:config', updatedConfig)

      // Atualizar parâmetros na store se sensorIndex estiver disponível
      if (props.sensorIndex !== undefined) {
        registerStore.updateAnalysisParameters(props.sensorIndex, updatedConfig)
      }
    }
  } catch {
    // Erro ao carregar mixing settings do sensor
  }
}

// Função para atualizar dados do sensor na store após salvar
const updateSensorInStore = async () => {
  if (!props.sensorData?.sensor?.id) return

  try {
    // Salvar configurações na API
    const success = await registerStore.saveMixingCycleSettings(props.sensorData.sensor.id, config.value)

    if (success) {
      // Recarregar dados do sensor para atualizar mixing_cycle_settings
      const updatedSettings = await registerStore.loadMixingCycleSettings(props.sensorData.sensor.id)

      if (updatedSettings) {
        // Atualizar o sensor na lista de energyReadings
        const energyReadings = registerStore.getEnergyReading
        const sensorIndex = energyReadings.findIndex((reading) => reading.sensor.id === props.sensorData.sensor.id)

        if (sensorIndex !== -1) {
          // Atualizar mixing_cycle_settings no sensor usando type assertion
          const sensor = energyReadings[sensorIndex].sensor as any
          sensor.mixing_cycle_settings = updatedSettings

          // Emitir evento para notificar que o sensor foi atualizado
          emit('sensor-updated')
        }
      }
    }
  } catch {
    // Erro ao atualizar sensor na store
  }
}

const resetConfig = () => {
  const defaultConfig = {
    window_period: '1m',
    min_cycle_duration: 20,
    max_cycle_duration: 350,
    change_percentile: 70,
    min_data_points: 10,
    smoothing_factor: 1.0,
    use_neighbor_average: true,
    rate_multiplier: 2.0,
  }

  emit('update:config', defaultConfig)

  // Atualizar parâmetros na store se sensorIndex estiver disponível
  if (props.sensorIndex !== undefined) {
    registerStore.updateAnalysisParameters(props.sensorIndex, defaultConfig)
  }
}

const executeWithCurrentConfig = () => {
  emit('execute', config.value)
}

// Carregar mixing settings quando o componente for montado
onMounted(() => {
  if (props.sensorData?.sensor?.id) {
    loadSensorMixingSettings()
  }
})

// Watcher para carregar mixing settings quando sensorData mudar
watch(
  () => props.sensorData?.sensor?.id,
  (newSensorId) => {
    if (newSensorId) {
      loadSensorMixingSettings()
    }
  },
)

// Expor função para atualizar sensor (pode ser chamada pelo componente pai)
defineExpose({
  updateSensorInStore,
  loadSensorMixingSettings,
})
</script>
